import { useState } from "react";
import { useLocation, Outlet } from "react-router-dom";
import { X } from "lucide-react";
import { useAuth } from "../hooks/useAuth";
import { filterMenuItems, getUserAccessLevel } from "../lib/permissions";
import { useTranslation } from "react-i18next";
import { useCurrentTime } from "../hooks/useCurrentTime";
import { buildMenuItems } from "./layout/buildMenuItems";
import CompanyLogo from "./layout/CompanyLogo";
import SidebarNavMenu from "./layout/SidebarNavMenu";
import SidebarSystemCard from "./layout/SidebarSystemCard";
import SidebarUserProfile from "./layout/SidebarUserProfile";
import LayoutHeader from "./layout/LayoutHeader";
import LayoutFooter from "./layout/LayoutFooter";
import i18n from "../i18n/index";

const Layout = ({ children }) => {
  const location = useLocation();
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const { formatFullDateTime } = useCurrentTime();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const isRTL = i18n.language === "ar";

  const allMenuItems = buildMenuItems(t);
  const menuItems = filterMenuItems(user, allMenuItems);
  const userAccessLevel = getUserAccessLevel(user);

  const sidebarContent = (
    <>
      <div className="flex items-center h-14 px-5" style={{ borderBottom: "1px solid var(--lm-border)" }}>
        <CompanyLogo showCompanyName={true} />
      </div>
      <div className="flex-1 px-3 py-4 overflow-y-auto">
        <SidebarNavMenu menuItems={menuItems} pathname={location.pathname} />
        <div className="mt-4 pt-4" style={{ borderTop: "1px solid var(--lm-border)" }}>
          <SidebarSystemCard />
        </div>
      </div>
      <SidebarUserProfile user={user} userAccessLevel={userAccessLevel} logout={logout} />
    </>
  );

  return (
    <div className="flex h-screen" style={{ backgroundColor: "var(--lm-canvas)", fontFamily: isRTL ? "'Cairo', 'Inter', system-ui, sans-serif" : "Inter, system-ui, sans-serif" }}>
        {/* Desktop Sidebar */}
        <div
          className="lm-sidebar-desktop hidden lg:flex lg:flex-col"
          style={{
            width: "240px", flexShrink: 0, backgroundColor: "var(--lm-s1)",
            borderRight: isRTL ? "none" : "1px solid var(--lm-border)",
            borderLeft: isRTL ? "1px solid var(--lm-border)" : "none",
            order: isRTL ? 1 : 0,
          }}
        >
          {sidebarContent}
        </div>

        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div className="fixed inset-0 z-50 lg:hidden">
            <div className="fixed inset-0" style={{ backgroundColor: "rgba(0,0,0,0.6)" }} onClick={() => setSidebarOpen(false)} />
            <div
              className="lm-sidebar-mobile fixed inset-y-0 flex flex-col"
              style={{
                width: "240px", backgroundColor: "var(--lm-s1)",
                left: isRTL ? "auto" : 0,
                right: isRTL ? 0 : "auto",
                borderRight: isRTL ? "none" : "1px solid var(--lm-border)",
                borderLeft: isRTL ? "1px solid var(--lm-border)" : "none",
              }}
            >
              <div className="flex items-center justify-between h-14 px-5" style={{ borderBottom: "1px solid var(--lm-border)" }}>
                <CompanyLogo showCompanyName={true} />
                <button onClick={() => setSidebarOpen(false)} className="flex items-center justify-center w-7 h-7 rounded-lg transition-colors"
                  style={{ color: "var(--lm-ink-3)" }}
                  onMouseEnter={e => (e.currentTarget.style.backgroundColor = "var(--lm-s2)")}
                  onMouseLeave={e => (e.currentTarget.style.backgroundColor = "transparent")}>
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="flex-1 px-3 py-4 overflow-y-auto">
                <SidebarNavMenu menuItems={menuItems} pathname={location.pathname} onNavClick={() => setSidebarOpen(false)} />
              </div>
              <SidebarUserProfile user={user} userAccessLevel={userAccessLevel} logout={logout} />
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="flex-1 flex flex-col min-w-0">
          <LayoutHeader
            setSidebarOpen={setSidebarOpen}
            menuItems={menuItems}
            pathname={location.pathname}
            formatFullDateTime={formatFullDateTime}
            user={user}
            userAccessLevel={userAccessLevel}
            logout={logout}
          />
          <main className="flex-1 overflow-auto p-6" style={{ backgroundColor: "var(--lm-canvas)" }}>
            <Outlet />
            {children}
            <LayoutFooter />
          </main>
        </div>
      </div>
  );
};

export default Layout;
